import $ from 'jquery'
import camelCase from 'lodash/camelCase'
import upperFirst from 'lodash/upperFirst'
import { storeToRefs } from 'pinia'

import { onNextPageTeardown } from '@app/adapters/page-teardown'

import { INPUTS_WITH_PREVIEW_SELECTORS } from '../constants'
import LiveInputActionRow from '../components/live-input-action-row'
import LiveInputsTextInput from './live_inputs/text_input'
import LiveInputsDateInput from './live_inputs/date_input'
import LiveInputsBooleanInput from './live_inputs/boolean_input'
import LiveInputsRadioInput from './live_inputs/radio_input'
import LiveInputsSelectInput from './live_inputs/select_input'
import LiveInputsFileCollectionInput from './live_inputs/file_collection_input'
import LiveInputsPostLink from './live_inputs/post_link'
import LiveInputsPostNewRowLink from './live_inputs/post_new_row_link'
import makeLiveInputActions from './live-input-actions'
import { createEvent } from '../../../utils/domHelpers'
import { getQueryParam } from '@app/utils/url-helpers'
import getPiniaInstance from '@app/shared/pinia/instance'
import {
  liveInputEvents,
  LIVE_INPUT_ASYNC_TEMPLATE_FETCH_TIMEOUT,
} from '../constants'
import { scrollToLiquidPathUrl } from '../../communication/behaviors/focus-text-property-field-from-url'
import { fixAnchorScrolling } from '@app/application/behaviors/setup-turbolinks-navigation-fixes'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import { renderFlameGraphs } from '@app/application/behaviors/flame-graph'
import { askUnreviewConfirmation } from '@app/shared/services/data-alteration-guard'
import setupAutoreconciliationModal from '@app/application/behaviors/auto-reconciliation-modal'
import { useContext } from '@app/shared/pinia/context'
import { withTimeout } from '@app/utils/wait-until-timeout'
import { abort, getSignal } from '@app/adapters/abort'

export default class LiveInputs {
  constructor(container, locked) {
    this.on = this.on.bind(this)
    this.off = this.off.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.autoSubmit = this.autoSubmit.bind(this)
    this.refocusActiveElement = this.refocusActiveElement.bind(this)
    this.saveChanges = this.saveChanges.bind(this)
    this.teardown = this.teardown.bind(this)
    this.unsubscribers = []
    this.liveValidationHandlers = []
    this.showCompanyDefaultHandlers = []
    this.fileCollectionActiveElementHandler = null

    this.locked = locked
    this.$container = $(container)
    this.propertiesPath = this.$container.data('create-properties-path')
    this.attemptToConfirmUnreview = this.$container.data(
      'attempt-to-confirm-unreview'
    )
    this.undoPropertiesPath = this.$container.data('undo-properties-path')
    this.fileAttachmentsAsModal = this.$container.data(
      'file-attachments-as-modal'
    )
    this.dataToSubmit = []
    this.deleteClassName = 'sf-markdown-delete'

    this.asyncRenderPath = this.$container.data('async-render-path')
    this.firstAsyncPageRender = false

    this.liveInputActions = makeLiveInputActions({ LiveInputActionRow })
    this.liveInputActions.initialize()

    const onFocusLiveInput = (input) => {
      this.focus($(input))
      return window.eventHub.$off('focus-live-input', onFocusLiveInput)
    }

    window.eventHub.$on('focus-live-input', onFocusLiveInput)

    window.eventHub.$on('drawer:animation-start', this.liveInputActions.unmount)
    window.eventHub.$on('drawer:animation-end', this.refocusActiveElement)

    this.unsubscribers.push(() => {
      window.eventHub.$off(
        'drawer:animation-start',
        this.liveInputActions.unmount
      )

      window.eventHub.$off('drawer:animation-end', this.refocusActiveElement)
    })

    window.eventHub.$on('saveChanges', this.saveChanges)
    this.unsubscribers.push(() => {
      window.eventHub.$off('saveChanges', this.saveChanges)
    })

    if (this.locked) {
      const fileCollectionInputs = new LiveInputsFileCollectionInput(
        this,
        this.locked
      )
      this.initializePreviewActions()

      this.unsubscribers.push(() => {
        fileCollectionInputs.teardown()
      })
    } else {
      const textInputs = new LiveInputsTextInput(this)
      const dateInputs = new LiveInputsDateInput(this)
      const booleanInputs = new LiveInputsBooleanInput(this)
      const radioInputs = new LiveInputsRadioInput(this)
      const selectInputs = new LiveInputsSelectInput(this)
      const fileCollectionInputs = new LiveInputsFileCollectionInput(
        this,
        this.locked
      )
      const postLinks = new LiveInputsPostLink(this)
      const postnewRowLinks = new LiveInputsPostNewRowLink(this)

      this.liveValidationHandlers.push(() => {
        textInputs.showInputValidations()
        dateInputs.showInputValidations()
        selectInputs.showInputValidations()
      })

      this.fileCollectionActiveElementHandler = () => {
        fileCollectionInputs.resetActiveFileElement()
      }

      this.showCompanyDefaultHandlers.push(() => {
        textInputs.showExternalCompanyDefaults()
        dateInputs.showExternalCompanyDefaults()
        selectInputs.showExternalCompanyDefaults()
      })

      this.unsubscribers.push(() => {
        textInputs.teardown()
        dateInputs.teardown()
        booleanInputs.teardown()
        radioInputs.teardown()
        selectInputs.teardown()
        fileCollectionInputs.teardown()
        postLinks.teardown()
        postnewRowLinks.teardown()
      })

      this.resizeInputs()
      this.resizeTextAreas()

      $(document).on('keydown', null, this.handleSubmit)
      onNextPageTeardown(() => {
        $(document).off('keydown', this.handleSubmit)
        this.liveInputActions.teardown()
      })
    }
  }

  completeInitialization() {
    window.eventHub.$emit('sf-live-inputs:initialized')
  }

  supportsAsyncTemplateRendering() {
    return !!this.asyncRenderPath
  }

  clearDataToSubmit() {
    this.dataToSubmit = []
  }

  teardown() {
    this.unsubscribers.forEach((unsubscriber) => {
      unsubscriber()
    })

    this.liveValidationHandlers = []
    this.showCompanyDefaultHandlers = []
  }

  autoSubmit(successCallback, errorCallback) {
    this.disableInputs()

    const onSuccess = () => {
      if (successCallback) {
        successCallback()
      }
    }

    const onError = (xhr, status, error) => {
      this.enableInputs()

      if (errorCallback) {
        errorCallback(xhr, status, error)
      }
    }

    this.postUpdate(onSuccess, onError)
  }

  disableInputs() {
    // An attribute, because enableInputs finds these again with an attribute
    // selector. `prop` sets a JavaScript property that no selector can see.
    return this.$container
      .find('input, select, textarea')
      .not('[disabled]')
      .prop('disabled', true)
      .prop('readonly', true)
      .attr('data-live-inputs-disabled-for-auto-submit', 'true')
  }

  enableInputs() {
    return this.$container
      .find('[data-live-inputs-disabled-for-auto-submit]')
      .prop('disabled', false)
      .prop('readonly', false)
      .removeAttr('data-live-inputs-disabled-for-auto-submit')
  }

  initializePreviewActions() {
    return INPUTS_WITH_PREVIEW_SELECTORS.map((liveInput) => {
      const selector = `[data-sf-input-type~='${liveInput}'][data-behaviour~='preview-inputs']`
      return this.on('mouseover', selector, (event) => {
        let target
        if (event.target.tagName === 'INS' || event.target.tagName === 'DEL') {
          target = $(event.target).closest("[data-behaviour='preview-inputs']")
        } else {
          target = $(event.target)
        }
        return this.focus(target)
      })
    })
  }

  requestOptions($element, overrides = {}) {
    const data = {
      value: $element.val(),
      value_type: $element.data('value-type'),
      switch_sign: $element.data('invert'),
      name: $element.attr('data-name'),
      object_id: $element.data('object-id'),
      object_type: $element.data('object-type'),
      object_ledger_id: $element.data('object-ledger-id'),
      precision: $element.data('precision'),
      format: $element.data('format'),
      ledger_id: this.$container.data('ledger-id'),
    }
    data[this.$container.data('owner-type')] = this.$container.data('owner-id')
    return $.extend(data, overrides)
  }

  update($element, options = {}) {
    const submit = options['submit']
    delete options['submit']

    const clearDataOnReject = options['clearDataOnReject']
    delete options['clearDataOnReject']

    const renderTooltips = options['renderTooltips']
    delete options['renderTooltips']

    const successCallback = options['successCallback']
    delete options['successCallback']

    const errorCallback = options['errorCallback']
    delete options['errorCallback']

    this.dataToSubmit.push(this.requestOptions($element, options))

    const shouldAutosave =
      $element[0]?.dataset?.autosave === 'true' ||
      // Checkboxes are wrapped with label, so we need to check parent
      $element[0]?.closest('.sf-form-check')?.dataset?.autosave === 'true'

    if (shouldAutosave) {
      this.autoSubmit(successCallback, errorCallback, { renderTooltips })
      return
    }

    if (!submit) {
      this.showSubmit()
      window.eventHub.$emit('sf-live-inputs:enableSaveChanges')
      return $element.addClass('update-pending')
    } else {
      return this.postUpdate(successCallback, errorCallback, {
        clearDataOnReject,
        renderTooltips,
      })
    }
  }

  hideAllButtons() {
    this.hideSubmit()
    this.hideSaved()
    this.hideUndo()
    this.hideRedo()
    this.hideFailed()
  }

  postUpdate(
    successCallback,
    errorCallback,
    { clearDataOnReject, renderTooltips } = {}
  ) {
    this.hideAllButtons()

    if (this.attemptToConfirmUnreview) {
      this.postUpdateWithUnreviewConfirmation(successCallback, errorCallback, {
        clearDataOnReject,
        renderTooltips,
      })
    } else {
      return this.performPostUpdate(successCallback, errorCallback, {
        renderTooltips,
      })
    }
  }

  async postUpdateWithUnreviewConfirmation(
    successCallback,
    errorCallback,
    { clearDataOnReject, renderTooltips } = {}
  ) {
    const { confirmed, skipUnprepare } = await askUnreviewConfirmation()

    if (!confirmed) {
      // autoSubmit disabled them. A successful save replaces them with the
      // re-rendered template and an error re-enables them. A refusal does
      // neither.
      this.enableInputs()

      // The data gets duplicated when the user rejects the unreview
      // confirmation dialog then tries it again with an approval
      if (clearDataOnReject) {
        this.clearDataToSubmit()
      }
      return
    }

    this.performPostUpdate(successCallback, errorCallback, {
      renderTooltips,
      skipUnprepare,
    })
  }

  performPostUpdate(
    successCallback,
    errorCallback,
    { renderTooltips, skipUnprepare } = {}
  ) {
    this.hideSubmit()

    const requestPayload = {
      properties: this.dataToSubmit,
      company_workflow_id: getQueryParam('company_workflow_id'),
      debug: getQueryParam('debug'),
      skip_unprepare: skipUnprepare || false,
    }

    return this.ajaxWithSpinner({
      type: 'POST',
      dataType: 'JSON',
      contentType: 'application/json',
      url: this.propertiesPath,
      data: JSON.stringify(requestPayload),
      success: (data) => {
        this.renderTemplate(data, true, renderTooltips)
        this.showUndo(data.audit_ids)
        this.clearDataToSubmit()
        this.triggerReconStatusUpdate(data.reconciliationSummaryHtml)

        window.eventHub.$emit('reloadWorkflowReviewStatusData', {
          skipUnprepare,
        })
        if (successCallback) {
          return successCallback()
        }
      },
      error: (xhr, status, error) => {
        this.clearDataToSubmit()
        if (errorCallback) {
          return errorCallback(xhr, status, error)
        }
      },
    })
  }

  triggerReconStatusUpdate(reconciliationSummaryHtml) {
    if (typeof reconciliationSummaryHtml === 'undefined') {
      return
    }

    const ownerType = this.$container.data('owner-type')
    const ownerId = this.$container.data('owner-id')
    let itemClass

    switch (ownerType) {
      case 'reconciliation_text_id':
        itemClass = 'reconciliation_text'
        break
      case 'account_entry_id':
        itemClass = 'account_entry'
        break
      default:
        return
    }

    if (itemClass && ownerId) {
      window.eventHub.$emit('sf-recon-status-updated', {
        itemClass,
        itemId: parseInt(ownerId, 10),
        reconciliationSummaryHtml,
      })
    }
  }

  postLastChangeUpdate(auditIds, hideButtonCallback, postLastChangeCallback) {
    hideButtonCallback()

    return this.ajaxWithSpinner({
      type: 'POST',
      dataType: 'JSON',
      contentType: 'application/json',
      url: this.undoPropertiesPath,
      data: JSON.stringify({
        audit_ids: auditIds,
        company_workflow_id: getQueryParam('company_workflow_id'),
        debug: getQueryParam('debug'),
      }),
      success: (data) => {
        this.renderTemplate(data)
        this.clearDataToSubmit()

        postLastChangeCallback.bind(this)(data.audit_ids)
      },
      error: () => {
        this.clearDataToSubmit()
      },
    })
  }

  delete($element, focusInput = true) {
    this.hideAllButtons()
    if ($element.data('localized') && !$element.data('in-loop')) {
      return this.update($element, { value: '', submit: true })
    } else {
      if (this.attemptToConfirmUnreview) {
        this.deleteWithUnreviewConfirmation($element, focusInput)
      } else {
        return this.performDelete($element, focusInput)
      }
    }
  }

  async deleteWithUnreviewConfirmation($element, focusInput = true) {
    this.handleDeleteActionMouseLeave()

    const { confirmed } = await askUnreviewConfirmation()
    if (!confirmed) return

    this.performDelete($element, focusInput)
  }

  performDelete($element, focusInput = true) {
    if ($element.length === 0) return

    const data = {
      name: $element.data('name'),
      object_id: $element.data('object-id'),
      object_type: $element.data('object-type'),
      object_ledger_id: $element.data('object-ledger-id'),
      ledger_id: this.$container.data('ledger-id'),
      _method: 'delete',
      company_workflow_id: getQueryParam('company_workflow_id'),
      debug: getQueryParam('debug'),
    }

    return this.ajaxWithSpinner({
      type: 'POST',
      url: this.propertiesPath,
      data,
      success: (data) => {
        this.renderTemplate(data, focusInput, false)
        this.triggerReconStatusUpdate(data.reconciliationSummaryHtml)
        this.showUndo(data.audit_ids)
        window.eventHub.$emit('reloadWorkflowReviewStatusData')
      },
    })
  }

  renderTemplate(data, focusInput = true, renderTooltips = true) {
    this.replaceHtml(data, focusInput)
    if (renderTooltips) {
      $(document).trigger('tooltipsRendered')
    }

    this.liveValidationHandlers.forEach((handler) => {
      handler()
    })

    this.fileCollectionActiveElementHandler?.()

    if (this.supportsAsyncTemplateRendering()) {
      this.handleAsyncTemplate(data)
    }
  }

  handleAsyncTemplate(data) {
    if (this.firstAsyncPageRender) {
      scrollToLiquidPathUrl()
      fixAnchorScrolling()
      this.firstAsyncPageRender = false
    }

    if (data.autoreconcileModalHtml) {
      const autoreconcileModalWrapper = document.querySelector(
        '#auto-reconcilie-modal-wrapper'
      )

      if (autoreconcileModalWrapper) {
        autoreconcileModalWrapper.innerHTML = ''
        autoreconcileModalWrapper.insertAdjacentHTML(
          'afterbegin',
          data.autoreconcileModalHtml
        )

        setupAutoreconciliationModal()
      }
    }

    if (data.autoreconcileModalButtonHtml) {
      const autoreconcileModalButtonWrapper = document.querySelector(
        '#auto-reconcilie-modal-button-wrapper'
      )

      if (autoreconcileModalButtonWrapper) {
        autoreconcileModalButtonWrapper.innerHTML = ''
        autoreconcileModalButtonWrapper.insertAdjacentHTML(
          'afterbegin',
          data.autoreconcileModalButtonHtml
        )
      }
    }

    if (data.debugDataHtml) {
      const liquidDebugWrapper = document.querySelector('#liquid-debug-wrapper')

      if (liquidDebugWrapper) {
        liquidDebugWrapper.innerHTML = ''
        liquidDebugWrapper.insertAdjacentHTML('afterbegin', data.debugDataHtml)

        renderFlameGraphs(liquidDebugWrapper)
      }
    }

    if (data.showPeopleDataSyncWarning) {
      const banner = document.getElementById('people-data-sync-banner')
      if (banner) {
        banner.classList.remove('sf-hidden')
      }
    }

    if (data.silentLiquidErrorsHtml) {
      const silentLiquidErrorsWrapper = document.querySelector(
        '#silent-liquid-errors-wrapper'
      )

      if (silentLiquidErrorsWrapper) {
        silentLiquidErrorsWrapper.innerHTML = ''
        silentLiquidErrorsWrapper.insertAdjacentHTML(
          'afterbegin',
          data.silentLiquidErrorsHtml
        )
      }
    }
  }

  async initAsyncRendering() {
    const timeBoundJson = withTimeout(
      SFetch.json,
      LIVE_INPUT_ASYNC_TEMPLATE_FETCH_TIMEOUT
    )

    try {
      const responseData = await timeBoundJson(this.asyncRenderPath, {
        method: 'POST',
        signal: getSignal('asyncRender'),
      })

      this.firstAsyncPageRender = true
      this.renderTemplate(responseData)
    } catch (error) {
      if (error === 'Timeout') {
        abort('asyncRender', 'Timeout')
        window.eventHub.$emit('sf-live-inputs:asyncRenderTimeout')
      } else {
        const htmlError = `
        <div class="tw-text-center">
          <h1>Something went wrong</h1>
          <p>We couldn't display this template due to a server error.</p>
        </div>
      `
        this.$container.html(htmlError).initializeDom()
      }

      SfLog.warn('Error in async rendering', {
        extra: {
          error,
        },
      })

      if (document.querySelector('[data-behaviour="reconciliation-summary"]')) {
        document.querySelector(
          '[data-behaviour="reconciliation-summary"]'
        ).innerHTML = ''
      }
    } finally {
      this.completeInitialization()
    }
  }

  on(eventName, filter, method) {
    return this.$container.on(eventName, filter, (...eventAndArguments) => {
      return method(...Array.from(eventAndArguments || []))
    })
  }

  off(eventName, filter, method) {
    return this.$container.off(eventName, filter, (...eventAndArguments) => {
      return method(...Array.from(eventAndArguments || []))
    })
  }

  setActiveElement($element) {
    return (this.$activeElement = $element)
  }

  removeNamespaceSelectedClass() {
    return this.allElements().removeClass('namespace-selected')
  }

  focus($element) {
    if (!$element || !$element.length) {
      return
    }

    const elementToFocus = $element.attr('data-name')
      ? $element
      : $element.closest('[data-name]')

    if (!elementToFocus.length) {
      return
    }

    const properties = {
      objectId: elementToFocus.data('objectId'),
      objectType: elementToFocus.data('objectType'),
      name: elementToFocus.attr('data-name'),
    }
    this.setActiveElement(elementToFocus)
    this.removeNamespaceSelectedClass()
    this.elementsForProperties(properties).addClass('namespace-selected')
    return this.mountActions(elementToFocus, properties)
  }

  refocusActiveElement() {
    window.requestAnimationFrame(() => {
      this.focus(this.$activeElement)
    })
  }

  saveChanges(options) {
    this.postUpdate(options?.successCallback, options?.errorCallback)
  }

  setTabDirection(value) {
    return (this.tabDirection = value)
  }

  ajaxWithSpinner(settings) {
    this.showSpinner()
    window.eventHub.$emit('sf-live-inputs:savingChanges')
    const jqXHR = $.ajax(settings)
    jqXHR.fail(() => {
      this.showFailed()
      window.eventHub.$emit('sf-live-inputs:saveChangesFailed')
    })
    return jqXHR.done(() => {
      this.showSaved()
      window.eventHub.$emit('sf-live-inputs:savedChanges')
    })
  }

  replaceHtml(data, focusInput = true) {
    this.saveFocusedElement()
    $('.datepicker').remove()
    this.$container.html(data.html).initializeDom()
    this.resizeInputs()
    this.resizeTextAreas()
    if (focusInput) {
      this.setFocusedElement(data.changedTextPropertyKey)
    }
    if (data.reconciliationSummaryHtml) {
      $("[data-behaviour~='reconciliation-summary']").html(
        data.reconciliationSummaryHtml
      )
    }

    this.showCompanyDefaultHandlers.forEach((handler) => {
      handler()
    })

    const event = createEvent('linkInputComms')
    return window.dispatchEvent(event)
  }

  removeTrashcan($trashcan) {
    $trashcan.find('[data-behavior="handler"]').remove()
    $trashcan.children().each(function (index, child) {
      // forcing blur event because of this dom manipulation
      // when moving from a input text focus to a select focus
      // with click event, the blur event is not triggered on the
      // input text field, so just do it for the sake if it
      // will make sure that method (and all payload attachment for update)
      // will be called
      $(child).trigger('blur')
      return $(child).insertBefore($trashcan)
    })
    return $trashcan.remove()
  }

  handleUndoClick($element, defaultValue) {
    const self = this
    // To restore the default value for a localized input we
    // need to set the text property to '' so:
    //  - it exists and doesn't fallback to another locale
    //  - it is not present so the default is used.
    const value = $element.data('localized') ? '' : null

    return window.modalCitizen.openModal('restore-default-confirmation-modal', {
      isInput: $element.length > 0 && $element[0].nodeName === 'INPUT',
      defaultValue,
      saveCallback() {
        return self.update($element, { value, submit: true })
      },
    })
  }

  handleCommentClick($element) {
    const contextStore = useContext(getPiniaInstance())
    const { context } = storeToRefs(contextStore)

    // init in case the comms request (providing the firm tags) isn't ready yet
    const tags = window.allRemarkTags || {}

    window.modalCitizen.openModal('new-remark', {
      ledgerLabel: $('#ledger-label').html(),
      ledgerId:
        $element.data('objectLedgerId') ||
        this.$container.data('ledger-id') ||
        window.Silverfin.currentLedgerId(),
      ownerId: context.value.id,
      ownerType: upperFirst(camelCase(context.value.type)),
      textPropertyValue: $element.val() || $element.data('value'),
      liquidInputFieldPath: $element.data('name'),
      allRemarkTags: tags,
      // pulse users: no tags in the API response == hide tags.
      // We hide tags in Pulse inbox (regardless of user) via showTags,
      // but LiveInput isn't used in the Pulse inbox.
      showTags: Object.keys(tags).length,
    })
  }

  handleDeleteActionMouseEnter(properties) {
    this.allElements().removeClass(this.deleteClassName)
    return this.elementsForProperties(properties).addClass(this.deleteClassName)
  }

  handleDeleteActionMouseLeave() {
    return this.allElements().removeClass(this.deleteClassName)
  }

  mountActions($element, properties) {
    if (!$element || !$element.length) {
      return
    }

    const defaultValue = $element.data('default-value')
    const changedDefaultValue = defaultValue && $element.data('default-changed')
    const hasModifiedDefaultValue =
      defaultValue && !$element.data('default-changed')

    const liquidPath = $element.data('name')
    const liquidPathParts = liquidPath ? liquidPath.split('.') : []
    const inALoop = liquidPathParts.length > 2

    const isAccountNamespace = liquidPathParts[0]
      ? !isNaN(Number(liquidPathParts[0]))
      : false

    this.liveInputActions.mount($element.get(0), {
      handleCommentClick: (e) => {
        this.handleCommentClick($element)
        this.liveInputActions.unmount()

        return e
      },
      handleUndoClick: (e) => {
        if (!hasModifiedDefaultValue) {
          this.handleUndoClick($element, defaultValue)
        }

        this.liveInputActions.unmount()

        return e
      },
      handleTrashClick: (e) => {
        this.delete($element)
        this.liveInputActions.unmount()

        return e
      },
      handleInsightsClick: () => {
        window.eventHub.$emit(liveInputEvents.INSIGHTS_ACTION_CLICKED, {
          reconciliationTextId: this.$container.data('owner-original-id'),
          periodDate: this.$container.data('period-date'),
          liquidPath,
        })
      },
      handleTrashMouseEnter: (e) => {
        this.handleDeleteActionMouseEnter(properties)

        return e
      },
      handleTrashMouseLeave: (e) => {
        this.handleDeleteActionMouseLeave()

        return e
      },
      hasCommentIcon:
        this.showFeatureInputFieldComms() &&
        !this.remarkForLiquidPathExists($element.data('name')),
      hasTrashIcon: !this.locked && (inALoop || !defaultValue),
      hasUndoIcon: !this.locked && changedDefaultValue,
      hasInsightsIcon:
        !inALoop &&
        !isAccountNamespace &&
        this.$container.data('owner-type') === 'reconciliation_text_id',
      isLocked: this.locked,
    })
  }

  saveFocusedElement() {
    const $documentActiveElement = $(document.activeElement)

    const $currentActiveElement = (() => {
      if (
        $documentActiveElement &&
        $.contains(this.$container, $documentActiveElement)
      ) {
        return $documentActiveElement
      } else if (this.$activeElement) {
        return this.$activeElement
      }
    })()

    if ($currentActiveElement) {
      this.focusedElementDataName = $currentActiveElement.attr('data-name')
      return (this.focusedElementIndex = this.$container
        .find(`[data-name='${this.focusedElementDataName}']`)
        .index($currentActiveElement))
    }
  }

  setFocusedElement(changedTextPropertyKey) {
    if (this.focusedElementDataName) {
      let elementDataNameToFocus
      const newRegex = /(\.|^)_new(\.|$)/
      if (newRegex.test(this.focusedElementDataName)) {
        elementDataNameToFocus = this.focusedElementDataName.replace(
          newRegex,
          `$1${changedTextPropertyKey}$2`
        )
      } else {
        const parts = this.focusedElementDataName.split('.')
        if (parts.length === 3) {
          parts[1] = changedTextPropertyKey
          elementDataNameToFocus = parts.join('.')
        } else {
          elementDataNameToFocus = this.focusedElementDataName
        }
      }
      const $focusedElement = this.$container
        .find(`[data-name='${elementDataNameToFocus}']`)
        .eq(this.focusedElementIndex)
      this.setActiveElement($focusedElement)

      if (this.tabDirection !== 0) {
        const $inputs = this.$container.find('input,textarea,select')
        $inputs
          .eq($inputs.index($focusedElement) + this.tabDirection)
          .focus()
          .select()
        return (this.tabDirection = 0)
      } else {
        return $focusedElement.focus()
      }
    }
  }

  elementsForProperties(properties) {
    const namespaceAndKey =
      properties.name?.split('.').slice(0, 2).join('.') ?? ''
    const filterForDataObjectAndType = `[data-object-id='${properties.objectId}'][data-object-type='${properties.objectType}']`
    return this.$container
      .find(
        `${filterForDataObjectAndType}[data-name^='${namespaceAndKey}.'],${filterForDataObjectAndType}[data-name='${namespaceAndKey}']`
      )
      .not(
        "[data-value-type='file_collection'], [data-behaviour='live-inputs-accounts-collection-input'], [data-behaviour='live-inputs-new-row-link']"
      )
  }

  allElements() {
    return this.$container.find('[data-object-id][data-object-type][data-name]')
  }

  resizeInputs() {
    return Array.from(
      this.$container.find('input,.sf-form-select-wrapper')
    ).map((input) => this.resizeInput($(input)))
  }

  resizeInput($input, updateWidth) {
    let contentLength
    const tagName = $input.prop('tagName')

    let fullWidth = false
    if ($input.is(`table ${tagName}`) && !$input.is('.input-mini')) {
      fullWidth = true

      // Go full-width when input is the sole leaf subnode of a table
      // cell (ignoring input's own descendants, e.g. <option>s).
      $input.parentsUntil('tr').each(function () {
        const content = $(this)
          .contents()
          .filter(function () {
            return (
              (this.nodeType === Node.ELEMENT_NODE &&
                this.dataset.behaviour !== 'bubble-placeholder' &&
                this.nodeName !== 'BR') ||
              (this.nodeType === Node.TEXT_NODE &&
                this.nodeValue.trim().length > 0)
            )
          })

        if (content.length > 1) {
          return (fullWidth = false)
        }
      })
    }

    if (fullWidth) {
      return $input.addClass('sf-full-width')
    } else if (
      updateWidth &&
      tagName === 'INPUT' &&
      (contentLength = $input.val().length) > 15
    ) {
      return $input.css('width', contentLength + 2 + 'ch')
    }
  }

  resizeTextAreas() {
    return Array.from(this.$container.find('textarea')).map((input) =>
      this.resizeTextArea($(input))
    )
  }

  resizeTextArea($input) {
    const height = Math.min($input.prop('scrollHeight'), 125)
    return $input.height(height)
  }

  handleSubmit(event) {
    if (
      event.key === 'Enter' &&
      !event.shiftKey && // prevent submitting on shift+enter when manual fori
      this.dataToSubmit.length > 0 &&
      (!$(event.target).is('textarea') || event.metaKey)
    ) {
      return this.postUpdate()
    }
  }

  showSubmit() {
    if ($('[data-behaviour="live-inputs-submit"]').length === 0) {
      Silverfin.Dirty.setDirty()
      this.hideAllButtons()
      // SVG comes from https://lucide.dev/icons/save
      return $(
        `<div class="tw-fixed tw-bottom-2 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-slate-600/80" data-behaviour="live-inputs-submit">
             <svg class="-tw-mb-[1px] tw-mr-[2px] tw-h-3 tw-w-3" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
               <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
             </svg>
             ${I18n.t('js.live_inputs.submit_message')}
           </div>`
      )
        .prependTo($('body'))
        .on('click', () => {
          return this.postUpdate()
        })
    }
  }

  hideSubmit() {
    return $('[data-behaviour="live-inputs-submit"]').remove()
  }

  showSpinner() {
    if ($('[data-behaviour="live-inputs-spinner"]').length === 0) {
      this.hideAllButtons()
      // SVG from https://tailwindcss.com/docs/animation#spin
      return $(
        `<div class="tw-fixed tw-bottom-2 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-slate-600/80" data-behaviour="live-inputs-spinner">
             <svg class="tw-animate-spin -tw-mb-[1px] tw-mr-1 tw-h-3 tw-w-3 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
               <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
               <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
             </svg>
             ${I18n.t('js.live_inputs.saving')}
           </div>`
      ).prependTo($('body'))
    }
  }

  showFailed() {
    if ($('[data-behaviour="live-inputs-spinner"]').length > 0) {
      this.hideSpinner()
      // SVG from https://lucide.dev/icons/x-circle
      return $(
        `<div class="tw-fixed tw-bottom-2 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-red-700/80" data-behaviour="live-inputs-failed">
             <svg class="-tw-mb-[1px] tw-mr-[2px] tw-h-3 tw-w-3"  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
               <circle cx="12" cy="12" r="10"/>
               <path d="m15 9-6 6"/><path d="m9 9 6 6"/>
             </svg>
             ${I18n.t('js.live_inputs.failed')}
           </div>`
      ).prependTo($('body'))
    }
  }

  showSaved() {
    if ($('[data-behaviour="live-inputs-saved"]').length === 0) {
      Silverfin.Dirty.clearDirty()
      this.hideSpinner()
      // SVG is check from https://heroicons.com
      return $(
        `<div class="tw-fixed tw-bottom-2 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-emerald-700/80" data-behaviour="live-inputs-saved">
             <svg class="tw-h-3 tw-w-3 -tw-mb-[1px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 13" fill="currentColor">
               <path fill-rule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clip-rule="evenodd" />
             </svg>
             ${I18n.t('js.live_inputs.saved')}
           </div>`
      ).prependTo($('body'))
    }
  }

  hideSaved() {
    return $('[data-behaviour="live-inputs-saved"]').remove()
  }

  hideSpinner() {
    return $('[data-behaviour="live-inputs-spinner"]').remove()
  }

  hideFailed() {
    return $('[data-behaviour="live-inputs-failed"]').remove()
  }

  showUndo(auditIds) {
    if (auditIds && auditIds.length > 0) {
      // SVG is arrow-uturn-left from https://heroicons.com
      return $(
        `<div class="tw-fixed tw-bottom-12 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-slate-600/80" data-behaviour="live-inputs-undo">
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="tw-h-3 -tw-mb-px">
               <path fill-rule="evenodd" d="M12.5 9.75A2.75 2.75 0 0 0 9.75 7H4.56l2.22 2.22a.75.75 0 1 1-1.06 1.06l-3.5-3.5a.75.75 0 0 1 0-1.06l3.5-3.5a.75.75 0 0 1 1.06 1.06L4.56 5.5h5.19a4.25 4.25 0 0 1 0 8.5h-1a.75.75 0 0 1 0-1.5h1a2.75 2.75 0 0 0 2.75-2.75Z" clip-rule="evenodd" />
             </svg>
             ${I18n.t('js.live_inputs.undo_message')}
           </div>`
      )
        .prependTo($('body'))
        .on('click', () => {
          return this.postLastChangeUpdate(
            auditIds,
            this.hideUndo,
            this.showRedo
          )
        })
    }
  }

  showRedo(auditIds) {
    if (auditIds && auditIds.length > 0) {
      // SVG is arrow-uturn-right from https://heroicons.com
      return $(
        `<div class="tw-fixed tw-bottom-12 tw-text-xs tw-right-2 tw-p-2 tw-z-[100000] tw-cursor-pointer tw-text-white tw-rounded-md tw-bg-slate-600/80" data-behaviour="live-inputs-redo">
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="tw-h-3 -tw-mb-px">
               <path fill-rule="evenodd" d="M3.5 9.75A2.75 2.75 0 0 1 6.25 7h5.19L9.22 9.22a.75.75 0 1 0 1.06 1.06l3.5-3.5a.75.75 0 0 0 0-1.06l-3.5-3.5a.75.75 0 1 0-1.06 1.06l2.22 2.22H6.25a4.25 4.25 0 0 0 0 8.5h1a.75.75 0 0 0 0-1.5h-1A2.75 2.75 0 0 1 3.5 9.75Z" clip-rule="evenodd" />
             </svg>
             ${I18n.t('js.live_inputs.redo_message')}
           </div>`
      )
        .prependTo($('body'))
        .on('click', () => {
          return this.postLastChangeUpdate(
            auditIds,
            this.hideRedo,
            this.showUndo
          )
        })
    }
  }

  hideUndo() {
    return $('[data-behaviour="live-inputs-undo"]').remove()
  }

  hideRedo() {
    return $('[data-behaviour="live-inputs-redo"]').remove()
  }

  showFeatureInputFieldComms() {
    const inputCommsOnWFReview =
      $('#wfreview-show-input-field-comms').length > 0
    const inputCommsOnAccountOrRT =
      $("[data-behaviour~='sidepane']").length > 0 &&
      $('#show-input-field-comms').length > 0

    // TODO remove showInputFieldComms when we roll this out
    return inputCommsOnWFReview || inputCommsOnAccountOrRT
  }

  remarkForLiquidPathExists(liquidPath) {
    return (
      $(document).find('[data-liquid-input-field-path="' + liquidPath + '"]')
        .length > 0
    )
  }
}
